import PropTypes from "prop-types";
import { isEmpty, isNil } from "ramda";
import { Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import PersonPresenter from "presenters/PersonPresenter";
import FacePresenter from "presenters/FacePresenter";
import PersonLabel from "components/PersonLabel";
import BalloonIcon from "assets/images/occasions/iconsComponents/Balloon";
import HeartIcon from "assets/images/occasions/iconsComponents/Heart";
import flowerBouquet from "assets/images/occasions/flowerBouquet.svg";
import flower from "assets/images/occasions/flower.svg";
import hat from "assets/images/occasions/hat.svg";
import { DATE_TYPE } from "enums";
import CTA from "components/CTA";
import partyHat from "assets/images/party-hat.png";
import { useScreen } from "hooks";

import DisplayFace from "../DisplayFace/DisplayFace";

import {
  Root,
  BlueBadge,
  GreenBadge,
  PartyHat,
  Balloon,
  BigHeart,
  SmallHeart,
  FlowerBouquet,
  Flower,
  FatherHat,
  BlueBalloon,
} from "./styles";

const DisplayPerson = ({
  showLabel = true,
  isCTA = false,
  person,
  face,
  onClick,
  relation,
  onCounterClick,
  onCheck,
  faceSelected,
  hasPartyHat = false,
  showBadge = true,
  variantCTA,
  noOpacity = false,
  dateType,
  showCheck = false,
}) => {
  const totalHeads = PersonPresenter.totalHeads(person) || 0;
  const { isDesktop, isMobile } = useScreen();

  const handleClick = () => {
    if (isNil(onClick)) return;

    onClick(person);
  };

  const getHat = () => {
    if (dateType === DATE_TYPE.fathersDay) {
      return <FatherHat src={hat} alt="FatherHat" />;
    }
    return hasPartyHat && <PartyHat src={partyHat} alt="PartyHat" />;
  };

  const getPersonDecoration = () => {
    switch (dateType) {
      case DATE_TYPE.birthday:
        return (
          <div>
            <Balloon as={BalloonIcon} style={{ bottom: 0, left: -20 }} color="#A3EAEB" />
            <Balloon as={BalloonIcon} style={{ top: 0, left: 25 }} color="#E5CBFC" />
            <Balloon as={BalloonIcon} style={{ top: 20, right: 5 }} color="#F578C2" />
            <BlueBalloon as={BalloonIcon} style={{ bottom: -46 }} color="#5192FF" />
          </div>
        );
      case DATE_TYPE.valentines:
        return (
          <div>
            <SmallHeart as={HeartIcon} style={{ bottom: isDesktop ? 70 : 40, left: isDesktop ? -20 : 0 }} />
            <BigHeart as={HeartIcon} style={{ top: isDesktop ? -40 : -10, left: isDesktop ? 25 : 20 }} />
            <BigHeart as={HeartIcon} style={{ top: isDesktop ? -20 : 15, right: 5 }} />
            <SmallHeart as={HeartIcon} style={{ bottom: isDesktop ? 30 : 5, right: 0 }} />
          </div>
        );
      case DATE_TYPE.anniversary:
        return (
          <div>
            <FlowerBouquet
              alt="Flower Bouquet"
              src={flowerBouquet}
              style={{
                transform: "scaleX(-1)",
                top: isMobile ? 7 : 35,
                left: -20,
              }}
            />
            <FlowerBouquet
              alt="Flower Bouquet"
              src={flowerBouquet}
              style={{
                top: isMobile ? 7 : 35,
                right: -20,
              }}
            />
          </div>
        );
      case DATE_TYPE.mothersDay:
        return (
          <div>
            <Flower alt="Flower" style={{ bottom: isDesktop ? 50 : 30, left: isDesktop ? -20 : -10 }} src={flower} />
            <Flower alt="Flower" style={{ top: isDesktop ? -40 : -10, left: isDesktop ? 25 : 20 }} src={flower} />
            <Flower alt="Flower" src={flower} style={{ top: isDesktop ? -20 : 15, right: 5 }} />
            <Flower alt="Flower" src={flower} style={{ bottom: isDesktop ? 30 : 5, right: isDesktop ? -20 : 0 }} />
          </div>
        );
      default:
        return null;
    }
  };

  const handleCheck = () => {
    if (showCheck) {
      let checkedFace;

      if (!isNil(person)) checkedFace = person;
      if (!isNil(face)) checkedFace = face;

      onCheck(isEmpty(faceSelected) || faceSelected !== checkedFace ? checkedFace : {});
    }
  };

  const isFaceSelected = faceSelected === person || faceSelected === face;

  return (
    <Root
      data-testid={`displayPerson-${PersonPresenter.id(person) || FacePresenter.id(face)}`}
      noOpacity={noOpacity}
      extended={!isNil(dateType)}
      onClick={handleCheck}
    >
      {getPersonDecoration()}
      {!showCheck && showBadge && totalHeads > 1 && (
        <BlueBadge
          component="span"
          variant="caption"
          data-testid={PersonPresenter.id(person)}
          onClick={onCounterClick ? () => onCounterClick(person) : handleClick}
          role="presentation"
        >
          <Typography component="small">{totalHeads > 99 ? "99+" : totalHeads}</Typography>
        </BlueBadge>
      )}
      {showCheck && isFaceSelected ? (
        <GreenBadge
          component="span"
          data-testid={`check-${PersonPresenter.id(person) || FacePresenter.id(face)}`}
          variant="caption"
          role="presentation"
        >
          <CheckCircleIcon />
        </GreenBadge>
      ) : null}
      {getHat()}
      {isCTA ? (
        <CTA relation={relation} showLabel={showLabel} variant={variantCTA} onClick={onClick} />
      ) : (
        <>
          <DisplayFace
            clickable={!isNil(onClick) || !isNil(onCheck)}
            face={isNil(face) ? PersonPresenter.defaultHead(person) : face}
            onClick={handleClick}
          />
          {showLabel && !isNil(person) && (
            <PersonLabel
              clickable={!isNil(onClick) || !isNil(onCheck)}
              label={PersonPresenter.label(person)}
              relation={PersonPresenter.relation(person)}
              onClick={handleClick}
            />
          )}
        </>
      )}
    </Root>
  );
};

DisplayPerson.propTypes = {
  hasPartyHat: PropTypes.bool,
  isCTA: PropTypes.bool,
  onClick: PropTypes.func,
  onCheck: PropTypes.func,
  faceSelected: PersonPresenter.shape() || FacePresenter.shape(),
  onCounterClick: PropTypes.func,
  person: PersonPresenter.shape(),
  face: FacePresenter.shape(),
  relation: PropTypes.string,
  showBadge: PropTypes.bool,
  showLabel: PropTypes.bool,
  variantCTA: PropTypes.string,
  noOpacity: PropTypes.bool,
  dateType: PropTypes.string,
  showCheck: PropTypes.bool,
};

export default DisplayPerson;
